import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import {
  ArrowLeft, Shield, User, Phone, Heart, FileText, ClipboardCheck,
  AlertTriangle, Home, CheckCircle, Lock, Check, Loader2, MessageSquare, Send, Download, Printer
} from "lucide-react";
import { safeJsonRequest } from "../utils/safeFetch";
import { generateIntakeDetailPDF } from "../utils/adminPdfExport";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Section = ({ title, icon: Icon, flagged, extraHeader, children }) => (
  <div className={`bg-slate-800 border rounded-xl overflow-hidden ${flagged ? "border-red-600" : "border-slate-700"}`}>
    <div className={`px-5 py-3 border-b flex items-center gap-2 flex-wrap ${flagged ? "border-red-600 bg-red-900/20" : "border-slate-700"}`}>
      {Icon && <Icon className={`w-4 h-4 ${flagged ? "text-red-400" : "text-blue-400"}`} />}
      <h3 className="text-sm font-bold text-white">{title}</h3>
      {flagged && <span className="text-xs font-bold px-2 py-0.5 rounded-full text-red-400 bg-red-900/40 ml-auto">FLAGGED</span>}
      {extraHeader && <div className={flagged ? "" : "ml-auto"}>{extraHeader}</div>}
    </div>
    <div className="px-5 py-4">{children}</div>
  </div>
);

const formatPhoneDisplay = (raw) => {
  const digits = String(raw || "").replace(/\D/g, "").slice(0, 10);
  if (!digits) return raw || "";
  if (digits.length < 10) return raw;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
};

const Field = ({ label, value, isPhone }) => {
  if (!value && value !== 0 && value !== false) return null;
  const display = typeof value === "boolean" ? (value ? "Yes" : "No") : isPhone ? formatPhoneDisplay(value) : String(value);
  return (
    <div>
      <dt className="text-[11px] text-slate-500 uppercase tracking-wider mb-0.5">{label}</dt>
      <dd className="text-sm text-slate-200 whitespace-pre-wrap">{display}</dd>
    </div>
  );
};

const INTAKE_STATUSES = [
  { value: "new", label: "New", color: "text-blue-400 bg-blue-900/40" },
  { value: "in_review", label: "In Review", color: "text-yellow-400 bg-yellow-900/40" },
  { value: "completed", label: "Completed", color: "text-green-400 bg-green-900/40" },
  { value: "archived", label: "Archived", color: "text-slate-400 bg-slate-700" },
];

const IntakeDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [intake, setIntake] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentStatus, setCurrentStatus] = useState("");
  const [statusLoading, setStatusLoading] = useState(false);
  const [notes, setNotes] = useState([]);
  const [newNote, setNewNote] = useState("");
  const [noteSending, setNoteSending] = useState(false);
  const [editingEffDate, setEditingEffDate] = useState(false);
  const [effDateValue, setEffDateValue] = useState("");
  const [effDateReason, setEffDateReason] = useState("");
  const [effDateSaving, setEffDateSaving] = useState(false);
  const [effDateError, setEffDateError] = useState("");
  const [safetyReviewLoading, setSafetyReviewLoading] = useState(false);

  const token = sessionStorage.getItem("admin_token");

  useEffect(() => {
    if (!token) { navigate("/admin"); return; }
    loadIntake();
  }, [id]);

  const loadIntake = async () => {
    setLoading(true);
    try {
      const data = await safeJsonRequest(`/api/admin/intakes/${id}`, { token });
      setIntake(data);
      setCurrentStatus(data.status || "new");
      setNotes(data.notes || []);
    } catch (err) {
      if (err.status === 401) navigate("/admin");
      else if (err.status === 404) navigate("/admin/dashboard");
    } finally { setLoading(false); }
  };

  const handleStatusChange = async (newStatus) => {
    if (newStatus === currentStatus) return;
    setStatusLoading(true);
    try {
      await safeJsonRequest(`/api/admin/intakes/${id}/status`, { method: "PATCH", body: { status: newStatus }, token });
      setCurrentStatus(newStatus);
    } catch {} finally { setStatusLoading(false); }
  };

  const handleAddNote = async () => {
    if (!newNote.trim()) return;
    setNoteSending(true);
    try {
      const data = await safeJsonRequest(`/api/admin/intakes/${id}/notes`, { method: "POST", body: { text: newNote.trim() }, token });
      setNotes((prev) => [...prev, data.note]);
      setNewNote("");
    } catch {} finally { setNoteSending(false); }
  };

  const handleDownloadPDF = () => {
    if (!intake) return;
    generateIntakeDetailPDF(intake);
  };

  const handleToggleSafetyReview = async () => {
    setSafetyReviewLoading(true);
    try {
      const data = await safeJsonRequest(`/api/admin/intakes/${id}/safety-review`, {
        method: "PATCH", body: { reviewed: !intake.safety_flag_reviewed }, token,
      });
      setIntake((prev) => ({ ...prev, safety_flag_reviewed: data.safety_flag_reviewed, safety_flag_reviewed_by: data.safety_flag_reviewed_by, safety_flag_reviewed_at: data.safety_flag_reviewed_at }));
    } catch {} finally { setSafetyReviewLoading(false); }
  };

  const openEffDateEditor = () => {
    setEffDateValue(cons.consentEffectiveDate || cons.signatureDate || "");
    setEffDateReason("");
    setEffDateError("");
    setEditingEffDate(true);
  };

  const handleSaveEffDate = async () => {
    setEffDateSaving(true);
    setEffDateError("");
    try {
      await safeJsonRequest(`/api/admin/intakes/${id}/consent-effective-date`, {
        method: "PATCH", body: { consent_effective_date: effDateValue, reason: effDateReason }, token,
      });
      setEditingEffDate(false);
      await loadIntake();
    } catch (err) {
      setEffDateError(err.detail || "Failed to update consent effective date");
    } finally { setEffDateSaving(false); }
  };

  const handleDownloadSignedPDF = async () => {
    if (!intake?.pdf_file_id) return;
    try {
      const resp = await fetch(`${API_URL}/api/intake/pdf/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) throw new Error("Download failed");
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `SDORA_Intake_${id.substring(0, 8)}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch { alert("Failed to download signed PDF"); }
  };

  if (loading) return <div className="min-h-screen bg-slate-900 flex items-center justify-center"><span className="w-8 h-8 border-3 border-blue-500/30 border-t-blue-500 rounded-full animate-spin" /></div>;
  if (!intake) return null;

  const fd = intake.form_data || {};
  const p = fd.personalInfo || {};
  const ec = fd.emergencyContact || {};
  const ins = fd.insurance || {};
  const ref = fd.referralInfo || {};
  const pc = fd.presentingConcerns || {};
  const mh = fd.medicalHistory || {};
  const ss = fd.safetyScreening || {};
  const sf = fd.socialFunctional || {};
  const cons = fd.consents || {};

  const clientName = [p.legalFirstName, p.legalMiddleName, p.legalLastName].filter(Boolean).join(" ") || "Unknown";
  const statusColor = INTAKE_STATUSES.find((s) => s.value === currentStatus)?.color || "text-slate-400 bg-slate-700";
  const statusLabel = INTAKE_STATUSES.find((s) => s.value === currentStatus)?.label || currentStatus;

  const cardImgUrl = (fileId) => fileId ? `${API_URL}/api/intake/card/${fileId}?token=${token}` : null;

  return (
    <div className="min-h-screen bg-slate-900 text-white print:bg-white print:text-black">
      <Helmet><meta name="robots" content="noindex, nofollow" /></Helmet>
      {/* Print-optimized styles */}
      <style>{`
        @media print {
          * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
          body { background: white !important; color: #111 !important; font-size: 10pt !important; }
          .print\\:hidden { display: none !important; }

          /* Reset dark backgrounds to white */
          [class*="bg-slate"], [class*="bg-gray"] { background: white !important; }
          [class*="border-slate"], [class*="border-gray"] { border-color: #e5e7eb !important; }

          /* Text colors */
          [class*="text-slate"], [class*="text-white"] { color: #111 !important; }
          [class*="text-blue"] { color: #1e40af !important; }
          [class*="text-red"] { color: #dc2626 !important; }
          [class*="text-green"] { color: #16a34a !important; }
          [class*="text-yellow"] { color: #ca8a04 !important; }
          [class*="text-amber"] { color: #b45309 !important; }
          [class*="text-teal"] { color: #0f766e !important; }

          /* Highlight sections */
          [class*="bg-red-900"] { background: #fef2f2 !important; border-color: #fecaca !important; }
          [class*="bg-blue-900"] { background: #eff6ff !important; border-color: #bfdbfe !important; }
          [class*="bg-amber-900"] { background: #fffbeb !important; border-color: #fde68a !important; }
          [class*="bg-green-900"] { background: #f0fdf4 !important; border-color: #bbf7d0 !important; }

          /* Hide navigation chrome */
          nav, footer, [data-testid="back-to-dashboard"], [data-testid="download-intake-pdf"],
          [data-testid="download-signed-pdf"], [data-testid="consent-legal-notice"],
          button, select { display: none !important; }

          /* Show status/source badges inline */
          [class*="rounded-full"] { border: 1px solid #d1d5db !important; background: #f9fafb !important; }

          /* Layout */
          .max-w-5xl { max-width: 100% !important; padding: 0 10mm !important; }
          .grid { display: grid !important; }
          .rounded-xl { border-radius: 4px !important; border: 1px solid #e5e7eb !important; }
          img { max-height: 100px !important; }

          /* Typography */
          h1 { font-size: 14pt !important; margin-bottom: 4pt !important; }
          h3 { font-size: 10pt !important; }
          .text-xs, .text-sm { font-size: 9pt !important; }

          /* Page setup */
          @page { margin: 12mm 10mm; size: A4; }

          /* Prevent section breaks */
          section, [data-testid*="section"] { break-inside: avoid; }

          /* Print header */
          .print-header { display: block !important; text-align: center; margin-bottom: 8mm; border-bottom: 1pt solid #1e3a5f; padding-bottom: 4mm; }
          .print-header h2 { font-size: 12pt; color: #1e3a5f; margin: 0; }
          .print-header p { font-size: 8pt; color: #6b7280; margin: 2pt 0 0; }
        }
        .print-header { display: none; }
      `}</style>

      {/* Print-only header */}
      <div className="print-header">
        <h2>SDORA Wellbeing LLC — Intake Record</h2>
        <p>1700 Reisterstown Rd, Suite 109, Pikesville, MD 21208 | (877) 772-5150 | Confidential</p>
        <p style={{ marginTop: '4pt' }}>Client: {clientName} | Status: {statusLabel} | {intake.submitted_at ? new Date(intake.submitted_at).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" }) : "Draft"}</p>
      </div>
      <div className="bg-slate-800 border-b border-slate-700 px-6 py-3 flex items-center gap-4 print:bg-white print:border-gray-300">
        <button onClick={() => navigate("/admin/dashboard")} className="p-2 rounded-lg hover:bg-slate-700 print:hidden" data-testid="back-to-dashboard"><ArrowLeft className="w-5 h-5 text-slate-400" /></button>
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <h1 className="text-lg font-bold">{clientName}</h1>
            {intake.safety_flag && (
              <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${intake.safety_flag_reviewed ? "text-green-400 bg-green-900/40" : "text-red-400 bg-red-900/40"}`} data-testid="header-safety-flag-badge">
                {intake.safety_flag_reviewed ? "SAFETY FLAG · REVIEWED" : "SAFETY FLAG · UNREVIEWED"}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2 mt-0.5">
            <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${statusColor}`}>{statusLabel}</span>
            <span className="text-xs text-slate-500">{ref.requestedService || "N/A"}</span>
            <span className="text-xs text-slate-600">|</span>
            <span className="text-xs text-slate-500">{intake.submitted_at ? new Date(intake.submitted_at).toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short" }) : "Draft"}</span>
          </div>
        </div>
        <button onClick={handleDownloadPDF} className="px-3 py-2 bg-emerald-700 hover:bg-emerald-600 rounded-lg text-xs text-white font-medium flex items-center gap-1.5 print:hidden" data-testid="download-intake-pdf">
          <Download className="w-4 h-4" /> Summary PDF
        </button>
        {intake.pdf_file_id && (
          <button onClick={handleDownloadSignedPDF} className="px-3 py-2 bg-blue-700 hover:bg-blue-600 rounded-lg text-xs text-white font-medium flex items-center gap-1.5 print:hidden" data-testid="download-signed-pdf">
            <Download className="w-4 h-4" /> Signed PDF
          </button>
        )}
        <button onClick={() => window.print()} className="px-3 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg text-xs text-white font-medium flex items-center gap-1.5 print:hidden" data-testid="print-intake-btn">
          <Printer className="w-4 h-4" /> Print
        </button>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-6 space-y-4">
        {/* Status */}
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs text-slate-400 mr-1">Status:</span>
          {INTAKE_STATUSES.map((opt) => (
            <button key={opt.value} onClick={() => handleStatusChange(opt.value)} disabled={statusLoading}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-all ${currentStatus === opt.value ? `${opt.color} border-current ring-1 ring-current/30` : "bg-slate-800 text-slate-400 border-slate-700 hover:border-slate-500"}`}
              data-testid={`intake-status-${opt.value}`}>
              {currentStatus === opt.value && <Check className="w-3 h-3 inline mr-1" />}{opt.label}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Section title="Personal Information" icon={User}>
            <dl className="grid grid-cols-2 gap-x-4 gap-y-3">
              <Field label="First Name" value={p.legalFirstName} />
              <Field label="Last Name" value={p.legalLastName} />
              <Field label="Middle Name" value={p.legalMiddleName} />
              <Field label="Preferred Name" value={p.preferredName} />
              <Field label="Date of Birth" value={p.dob} />
              <Field label="Sex at Birth" value={p.sexAtBirth} />
              <Field label="Gender Identity" value={p.genderIdentity} />
              <Field label="Pronouns" value={p.pronouns} />
              <Field label="Phone" value={p.phone} isPhone />
              <Field label="Email" value={p.email} />
              <Field label="Contact Preference" value={p.preferredContact} />
              <Field label="Language" value={p.languagePreference} />
              <Field label="Marital Status" value={p.maritalStatus} />
              <Field label="Race" value={p.race} />
              <Field label="Ethnicity" value={p.ethnicity} />
              <Field label="SSN (masked)" value={intake.ssn_masked} />
            </dl>
            <div className="mt-2 text-xs text-slate-500">
              <Field label="Address" value={[p.addressLine1, p.addressLine2, p.city, p.state, p.zip].filter(Boolean).join(", ")} />
            </div>
            {p.isMinor === "Yes" && (
              <div className="mt-3 p-2 bg-blue-900/20 rounded-lg">
                <p className="text-xs text-blue-400 font-bold mb-1">Minor — Guardian Info</p>
                <dl className="grid grid-cols-2 gap-x-4 gap-y-2">
                  <Field label="Guardian" value={p.guardianName} />
                  <Field label="Phone" value={p.guardianPhone} isPhone />
                  <Field label="Relationship" value={p.guardianRelationship} />
                </dl>
              </div>
            )}
          </Section>

          <Section title="Emergency Contact" icon={Phone}>
            <dl className="grid grid-cols-2 gap-x-4 gap-y-3">
              <Field label="Name" value={ec.ecName} />
              <Field label="Relationship" value={ec.ecRelationship} />
              <Field label="Phone" value={ec.ecPhone} isPhone />
              <Field label="Alt Phone" value={ec.ecAltPhone} isPhone />
              <Field label="Permission to Contact" value={ec.ecPermissionToContact} />
            </dl>
          </Section>

          <Section title="Insurance" icon={Shield}>
            <dl className="grid grid-cols-2 gap-x-4 gap-y-3">
              <Field label="Carrier / MCO" value={ins.insuranceCarrier} />
              <Field label="Member ID" value={ins.memberId} />
              <Field label="Group #" value={ins.groupNumber} />
              <Field label="Policyholder" value={ins.policyholderName} />
              <Field label="Policyholder DOB" value={ins.policyholderDob} />
              <Field label="Relationship" value={ins.policyholderRelationship} />
            </dl>
            {ins.hasSecondaryInsurance === "Yes" && (
              <div className="mt-2 pt-2 border-t border-slate-700">
                <p className="text-xs text-slate-500 mb-2">Secondary Insurance</p>
                <dl className="grid grid-cols-2 gap-x-4 gap-y-2">
                  <Field label="Carrier" value={ins.secondaryCarrier} />
                  <Field label="Member ID" value={ins.secondaryMemberId} />
                </dl>
              </div>
            )}
            {(ins.cardFrontId || ins.cardBackId) && (
              <div className="mt-3 pt-3 border-t border-slate-700">
                <p className="text-xs text-slate-500 mb-2">Insurance Card Images</p>
                <div className="grid grid-cols-2 gap-3">
                  {ins.cardFrontId && <img src={cardImgUrl(ins.cardFrontId)} alt="Card Front" className="rounded-lg border border-slate-600 max-h-40 object-contain bg-white" data-testid="card-front-img" />}
                  {ins.cardBackId && <img src={cardImgUrl(ins.cardBackId)} alt="Card Back" className="rounded-lg border border-slate-600 max-h-40 object-contain bg-white" data-testid="card-back-img" />}
                </div>
              </div>
            )}
          </Section>

          <Section title="Service Information" icon={FileText}>
            <dl className="grid grid-cols-2 gap-x-4 gap-y-3">
              <Field label="How Heard" value={ref.howHeard} />
              <Field label="Referring Provider" value={ref.referringProvider} />
              <Field label="Organization" value={ref.referringOrganization} />
              <Field label="Requested Service" value={ref.requestedService} />
              <Field label="Preferred Format" value={ref.preferredFormat} />
            </dl>
            {ref.omhcRequestedServices && ref.omhcRequestedServices.length > 0 && (
              <div className="mt-3 pt-2 border-t border-slate-700" data-testid="omhc-services-display">
                <p className="text-xs text-slate-500 mb-2 font-medium">OMHC Requested Services</p>
                <div className="flex flex-wrap gap-1.5">
                  {ref.omhcRequestedServices.map((svc) => (
                    <span key={svc} className="text-xs bg-blue-900/40 text-blue-300 px-2 py-0.5 rounded-full border border-blue-800">
                      {svc}
                    </span>
                  ))}
                </div>
                {ref.omhcOtherSpecify && (
                  <p className="text-xs text-slate-400 mt-1.5">Other: {ref.omhcOtherSpecify}</p>
                )}
              </div>
            )}
            <div className="mt-2"><Field label="Reason for Referral" value={ref.reasonForReferral} /></div>
          </Section>

          <Section title="Presenting Concerns" icon={Heart}>
            <div className="space-y-3">
              <Field label="Primary Concerns" value={pc.primaryConcerns} />
              <Field label="Symptoms" value={pc.symptoms} />
              <Field label="Onset" value={pc.onsetDate} />
              <Field label="Functioning Level" value={pc.functioningLevel} />
              <Field label="Treatment Goals" value={pc.treatmentGoals} />
              <Field label="Previous Treatment" value={pc.previousTreatment} />
              <Field label="Previous Hospitalizations" value={pc.previousHospitalizations} />
            </div>
          </Section>

          <Section title="Medical History" icon={ClipboardCheck}>
            <div className="space-y-3">
              <Field label="PCP" value={mh.primaryCareProvider} />
              <Field label="Current Diagnoses" value={mh.currentDiagnoses} />
              <Field label="Medical Conditions" value={mh.medicalConditions} />
              <Field label="Allergies" value={mh.allergies} />
              <Field label="Current Medications" value={mh.currentMedications} />
              <Field label="BH Providers" value={mh.currentBHProviders} />
              <Field label="Substance Use" value={mh.substanceUseHistory} />
              <Field label="Hospitalization History" value={mh.hospitalizationHistory} />
            </div>
          </Section>

          <Section
            title="Safety Screening"
            icon={AlertTriangle}
            flagged={intake.safety_flag}
            extraHeader={intake.safety_flag && (
              intake.safety_flag_reviewed ? (
                <button
                  onClick={handleToggleSafetyReview}
                  disabled={safetyReviewLoading}
                  className="text-xs font-medium px-2 py-1 rounded-lg text-green-400 bg-green-900/30 border border-green-800/50 hover:bg-green-900/50 disabled:opacity-50 flex items-center gap-1"
                  data-testid="safety-review-toggle"
                  title={`Reviewed by ${intake.safety_flag_reviewed_by || "staff"} on ${intake.safety_flag_reviewed_at ? new Date(intake.safety_flag_reviewed_at).toLocaleString() : ""}`}
                >
                  <Check className="w-3 h-3" /> Reviewed — Undo
                </button>
              ) : (
                <button
                  onClick={handleToggleSafetyReview}
                  disabled={safetyReviewLoading}
                  className="text-xs font-bold px-3 py-1 rounded-lg text-white bg-red-600 hover:bg-red-500 disabled:opacity-50"
                  data-testid="safety-review-toggle"
                >
                  {safetyReviewLoading ? "Saving..." : "Mark as Reviewed"}
                </button>
              )
            )}
          >
            <dl className="grid grid-cols-2 gap-x-4 gap-y-3">
              <Field label="Suicidal Thoughts" value={ss.suicidalThoughts} />
              <Field label="Self-Harm" value={ss.selfHarm} />
              <Field label="Harm to Others" value={ss.harmToOthers} />
              <Field label="Recent Crisis" value={ss.recentCrisis} />
              <Field label="Immediate Risk" value={ss.immediateSafetyRisk} />
            </dl>
            {ss.safetyDetails && <div className="mt-2"><Field label="Details" value={ss.safetyDetails} /></div>}
          </Section>

          <Section title="Social & Functional" icon={Home}>
            <dl className="grid grid-cols-2 gap-x-4 gap-y-3">
              <Field label="Housing" value={sf.housingStatus} />
              <Field label="Employment" value={sf.employmentStatus} />
              <Field label="Transportation" value={sf.transportationNeeds} />
              <Field label="Legal" value={sf.legalInvolvement} />
              <Field label="DJS" value={sf.djsInvolvement} />
            </dl>
            <div className="mt-2">
              <Field label="Support System" value={sf.supportSystem} />
              <Field label="Accessibility" value={sf.accessibilityNeeds} />
            </div>
          </Section>

          <Section title="Consents & Signature" icon={CheckCircle}>
            {fd.personalInfo?.isMinor === "Yes" ? (
              <Field label="Signing On Behalf of Minor As" value={cons.guardianType} />
            ) : (
              cons.hasLegalRepresentative === "Yes" && (
                <Field label="Legal Representative" value={`${cons.legalRepName || ""} (${cons.legalRepRelationship || ""})`} />
              )
            )}
            <div className="space-y-1 text-xs mt-2">
              {[["Item 1 — Consent to Treatment", cons.consentToTreatment], ["Item 2 — Privacy Practices", cons.privacyPractices],
                ["Item 3 — Electronic Communication", cons.electronicCommunication], ["Item 4 — Telehealth Policy", cons.telehealthConsent],
                ["Item 5 — Emergency Contact Auth", cons.emergencyContactAuth], ["Item 6 — Financial Responsibility", cons.financialResponsibility],
                ["Item 7 — Grievance & Complaint Procedure", cons.grievanceProcedure], ["Item 8 — Client Rights & Responsibilities", cons.clientRights],
              ].map(([lbl, val]) => (
                <div key={lbl} className="flex items-center gap-2">
                  {val ? <CheckCircle className="w-3 h-3 text-green-400" /> : <span className="w-3 h-3 rounded-full border border-slate-600 inline-block" />}
                  <span className={val ? "text-slate-200" : "text-slate-500"}>{lbl}</span>
                </div>
              ))}
            </div>
            <div className="mt-2 grid grid-cols-2 gap-x-4">
              <Field label="Item 9 — Service Delivery Preference" value={cons.serviceDeliveryPreference} />
              <Field label="Initials" value={cons.serviceDeliveryPreferenceInitials} />
            </div>
            <div className="mt-3 p-2.5 bg-slate-800/60 rounded-lg" data-testid="consent-prp-acknowledgment">
              {ref.requestedService === "PRP — Psychiatric Rehabilitation" ? (
                <>
                  <div className="flex items-center gap-2 text-xs mb-2">
                    {cons.prpAcknowledgment ? <CheckCircle className="w-3 h-3 text-green-400" /> : <span className="w-3 h-3 rounded-full border border-slate-600 inline-block" />}
                    <span className={cons.prpAcknowledgment ? "text-slate-200" : "text-slate-500"}>Item 10 — PRP Explained (Received in Writing)</span>
                  </div>
                  <Field label="Initials" value={cons.prpAcknowledgmentInitials} />
                </>
              ) : (
                <p className="text-xs text-slate-500 italic">Item 10 — Not applicable: no PRP services selected</p>
              )}
            </div>
            <div className="mt-3 grid grid-cols-2 gap-x-4">
              <Field label="Item 11 — Transportation Authorization" value={cons.transportationConsent} />
              <Field label="Initials" value={cons.transportationConsentInitials} />
            </div>
            <div className="mt-2 flex items-center gap-2 text-xs">
              {cons.rightToRefuse ? <CheckCircle className="w-3 h-3 text-green-400" /> : <span className="w-3 h-3 rounded-full border border-slate-600 inline-block" />}
              <span className={cons.rightToRefuse ? "text-slate-200" : "text-slate-500"}>Item 12 — Right to Refuse Treatment Acknowledged</span>
            </div>
            {cons.releaseOfInfoDetails && <div className="mt-3"><Field label="Release of Info" value={cons.releaseOfInfoDetails} /></div>}
            <div className="mt-3 p-3 bg-blue-900/20 rounded-lg">
              <Field label="Electronic Signature" value={cons.signatureText} />
              <dl className="grid grid-cols-2 gap-x-4 gap-y-2 mt-2">
                <Field label="Printed Name" value={cons.signaturePrintedName} />
                <Field label="Date" value={cons.signatureDate} />
                <Field label="Relationship" value={cons.signatureRelationship} />
              </dl>
            </div>
            <div className="mt-3 p-3 bg-slate-900/60 border border-slate-700 rounded-lg" data-testid="consent-effective-date-display">
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-bold text-slate-300">Consent Effective Date vs. Electronic Signature</p>
                {!editingEffDate && (
                  <button onClick={openEffDateEditor} className="text-[11px] text-blue-400 hover:text-blue-300 font-medium print:hidden" data-testid="edit-effective-date-btn">
                    Edit
                  </button>
                )}
              </div>
              {!editingEffDate ? (
                <dl className="grid grid-cols-2 gap-x-4 gap-y-2">
                  <Field label="Consent Effective Date" value={cons.consentEffectiveDate || cons.signatureDate} />
                  <Field label="Electronically Signed (actual, system-recorded)" value={intake.submitted_at ? new Date(intake.submitted_at).toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short" }) : ""} />
                </dl>
              ) : (
                <div className="space-y-2" data-testid="effective-date-editor">
                  <label className="block text-[11px] text-slate-400 mb-1">Consent Effective Date</label>
                  <input type="date" value={effDateValue} onChange={(e) => setEffDateValue(e.target.value)}
                    className="w-full px-2 py-1.5 bg-slate-700 border border-slate-600 rounded text-xs text-white"
                    data-testid="effective-date-input" />
                  <label className="block text-[11px] text-slate-400 mb-1 mt-2">Reason (required if earlier than signature date)</label>
                  <textarea value={effDateReason} onChange={(e) => setEffDateReason(e.target.value)} rows={2}
                    placeholder="Document why the consent effective date differs from the electronic signature date."
                    className="w-full px-2 py-1.5 bg-slate-700 border border-slate-600 rounded text-xs text-white placeholder-slate-500"
                    data-testid="effective-date-reason-input" />
                  {effDateError && <p className="text-[11px] text-red-400" data-testid="effective-date-error">{effDateError}</p>}
                  <div className="flex gap-2 mt-2">
                    <button onClick={handleSaveEffDate} disabled={effDateSaving} className="px-3 py-1.5 bg-blue-600 hover:bg-blue-500 rounded text-xs text-white font-medium disabled:opacity-50" data-testid="save-effective-date-btn">
                      {effDateSaving ? "Saving..." : "Save"}
                    </button>
                    <button onClick={() => setEditingEffDate(false)} className="px-3 py-1.5 bg-slate-700 hover:bg-slate-600 rounded text-xs text-slate-300" data-testid="cancel-effective-date-btn">
                      Cancel
                    </button>
                  </div>
                </div>
              )}
              {cons.effectiveDateReason && <p className="text-[11px] text-slate-400 mt-2">Reason on file: {cons.effectiveDateReason}</p>}
              {(cons.effectiveDateAmendments || []).length > 0 && (
                <div className="mt-2 pt-2 border-t border-slate-700" data-testid="effective-date-amendment-history">
                  <p className="text-[11px] text-slate-500 mb-1">Amendment History</p>
                  {cons.effectiveDateAmendments.map((a, idx) => (
                    <p key={idx} className="text-[11px] text-slate-500">
                      {a.previous_value || "(none)"} to {a.new_value} by {a.amended_by} on {a.amended_at ? new Date(a.amended_at).toLocaleDateString() : ""} — {a.reason}
                    </p>
                  ))}
                </div>
              )}
            </div>
            <div className="mt-3 p-2.5 bg-amber-900/20 border border-amber-800/40 rounded-lg" data-testid="consent-legal-notice">
              <p className="text-xs text-amber-400 font-semibold mb-1">Compliance Note</p>
              <p className="text-xs text-amber-300/80 leading-relaxed">
                Consent wording requires legal/compliance review before formal HIPAA compliance can be claimed.
                Acknowledgments use SDORA's existing approved language structure. Verify with your compliance officer.
              </p>
            </div>
          </Section>
        </div>

        {/* Notes */}
        <Section title={`Case Notes (${notes.length})`} icon={MessageSquare}>
          {notes.length > 0 && (
            <div className="space-y-2 mb-4 max-h-64 overflow-y-auto">
              {notes.map((n, idx) => (
                <div key={idx} className="bg-slate-900 rounded-lg px-3 py-2 border border-slate-700">
                  <p className="text-xs text-slate-200 whitespace-pre-wrap">{n.text}</p>
                  <div className="flex items-center gap-2 mt-1.5">
                    <span className="text-[10px] text-slate-500">{n.author}</span>
                    <span className="text-[10px] text-slate-600">{n.created_at ? new Date(n.created_at).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : ""}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
          <div className="flex gap-2">
            <input type="text" value={newNote} onChange={(e) => setNewNote(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleAddNote(); }}}
              placeholder="Add a case note..." className="flex-1 px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-xs text-white placeholder-slate-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              data-testid="intake-note-input" />
            <button onClick={handleAddNote} disabled={noteSending || !newNote.trim()} className="px-3 py-2 bg-blue-600 hover:bg-blue-500 rounded-lg disabled:opacity-50" data-testid="intake-note-send">
              {noteSending ? <Loader2 className="w-3.5 h-3.5 text-white animate-spin" /> : <Send className="w-3.5 h-3.5 text-white" />}
            </button>
          </div>
        </Section>
      </div>
    </div>
  );
};

export default IntakeDetailPage;
